import { Alert, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import constants from '../../constants';

// GH-826 — the member's devices (one row per app installation / browser) and, for Android, the daily
// step-recording check: did the phone's background step recording (Google Play Services) answer, and does the
// live count match what Google recorded. Read-only.

const fmt = (d?: string) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—');
const yesNo = (v?: boolean | null, yes = 'כן', no = 'לא') => (v === true ? yes : v === false ? no : '—');

const FLAG_LABELS: Record<string, string> = {
	subscribe_failed: 'הספירה ברקע לא הופעלה',
	live_gap: 'פער גדול בין הספירה החיה לרשומה',
	power_save: 'חיסכון סוללה פעיל',
};

const MemberDevicesTab = ({ memberId }: { memberId: string }) => {
	const [data, setData] = useState<{ devices: any[]; step_recording: any[] } | null>(null);
	const [error, setError] = useState('');

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await axios.get(`${constants.API_URL}member-devices/admin/member/${memberId}`, {
					headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
				});
				if (!cancelled) setData(res.data);
			} catch (e: any) {
				if (!cancelled) setError(e?.response?.status === 404 ? 'השרת עוד לא כולל את GH-826' : 'שגיאה בטעינה');
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [memberId]);

	if (error) return <Alert severity="warning">{error}</Alert>;
	if (!data) return <CircularProgress size={24} />;

	return (
		<Stack spacing={3} sx={{ width: '100%' }}>
			<Stack spacing={1}>
				<Typography variant="h6">מכשירים</Typography>
				{data.devices.length === 0 ? (
					<Typography color="text.secondary">
						אין עדיין מידע. המכשיר נרשם כשהמשתמש פותח גרסת אפליקציה שכוללת את GH-826 (או את האתר).
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>מכשיר</TableCell>
								<TableCell>מערכת הפעלה</TableCell>
								<TableCell>גרסת אפליקציה</TableCell>
								<TableCell>דפדפן</TableCell>
								<TableCell>נראה לראשונה</TableCell>
								<TableCell>נראה לאחרונה</TableCell>
								<TableCell>כניסות</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.devices.map((d) => (
								<TableRow key={d._id}>
									<TableCell>
										{[d.brand, d.model_name || d.model_id].filter(Boolean).join(' ') || d.platform || '—'}
										{d.is_device === false ? ' (אמולטור)' : ''}
									</TableCell>
									<TableCell>
										{[d.os_name, d.os_version].filter(Boolean).join(' ') || '—'}
										{d.api_level ? ` (API ${d.api_level})` : ''}
									</TableCell>
									<TableCell>{[d.app_version, d.app_build && `(${d.app_build})`].filter(Boolean).join(' ') || '—'}</TableCell>
									<TableCell>{[d.browser_name, d.browser_version].filter(Boolean).join(' ') || '—'}</TableCell>
									<TableCell>{fmt(d.first_seen_at)}</TableCell>
									<TableCell>{fmt(d.last_seen_at)}</TableCell>
									<TableCell>{d.seen_count ?? '—'}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Stack>

			<Stack spacing={1}>
				<Typography variant="h6">ספירת צעדים ברקע (אנדרואיד)</Typography>
				{data.step_recording.length === 0 ? (
					<Typography color="text.secondary">
						אין דיווחים. הם נשלחים רק מאנדרואיד, רק כשהמתג "בדיקת ספירת צעדים ברקע" דלוק בחברה, ורק למשתתפים.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>תאריך</TableCell>
								<TableCell>הפעלה מול Google</TableCell>
								<TableCell>כישלונות ביום</TableCell>
								<TableCell>צעדים: חי / רשום</TableCell>
								<TableCell>Health Connect</TableCell>
								<TableCell>חיסכון סוללה</TableCell>
								<TableCell>Play Services</TableCell>
								<TableCell>סימונים</TableCell>
								<TableCell>דיווח אחרון</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.step_recording.map((r) => (
								<TableRow key={r._id}>
									<TableCell>{r.date}</TableCell>
									<TableCell>
										{r.subscribe_ok === true ? 'הצליחה' : r.subscribe_ok === false ? `נכשלה: ${r.subscribe_code || ''}` : '—'}
										{r.subscribe_ok === false && r.subscribe_message ? (
											<Typography variant="caption" display="block" color="text.secondary">
												{r.subscribe_message}
											</Typography>
										) : null}
									</TableCell>
									<TableCell>{r.subscribe_failures ?? 0}</TableCell>
									<TableCell>
										{r.live_today_max ?? '—'} / {r.recorded_today ?? '—'}
									</TableCell>
									<TableCell>{yesNo(r.health_connected, 'מחובר', 'לא מחובר')}</TableCell>
									<TableCell>
										{r.power_save_mode === true ? 'פעיל' : r.power_save_mode === false ? 'כבוי' : '—'}
										{r.ignoring_battery_optimizations === true ? ' · ללא הגבלת רקע' : ''}
									</TableCell>
									<TableCell>{r.play_services_version ?? '—'}</TableCell>
									<TableCell>{(r.flags || []).map((f: string) => FLAG_LABELS[f] || f).join(', ') || '—'}</TableCell>
									<TableCell>{fmt(r.last_report_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Stack>
		</Stack>
	);
};

export default MemberDevicesTab;
